import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import APIServices from '../../../../services/APIServices';
import { formatDate, hasPermission, PAGE_LIMIT, selectLoader } from '../../../../utils/Helpers';
import SignOffForm from '../../forms/SignOffForm';
import SidebarModal from '../../../../components/SidebarModal';
import { useStore } from '../../../../hooks/useStore';
import ChangeOrderModal from '../../updatedOverviewModals/ChangeOrderModal';
import AddQuoteModal from '../../updatedOverviewModals/AddQuoteModal';
import RequestModal from '../../updatedOverviewModals/RequestModal';
import ModalCenter from 'components/ModalCenter';
import { useNavigate } from 'react-router-dom';

const RequestListTab = ({
  projectId,
  currentTab,
  onRequestClick,
  memberPermission,
  userRole,
  isInternalProject,
  projectTeam
}) => {
  const activeTabRef = useRef('open'); // Create a ref to store activeTab
  const scrollRef = useRef(null);
  const paginationRequestInProgressRef = useRef(false);
  const navigate = useNavigate();
  const [store, setStore] = useStore();
  const { user, isUser1, socket } = useAuth();
  const [isQouteRequired, setQouteRequired] = useState(false);
  const [changeReqId, setChangeReqId] = useState('');
  const [cache, setCache] = useState({ open: null, close: null });
  // const [projectId, setProjectId] = useState("");
  const [activeTab, setActiveTab] = useState('open');
  const [requests, setRequests] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // Default to page 1
  const [hasMore, setHasMore] = useState(true); // To track if more projects exists
  const [modals, setModals] = useState({
    signOffModal: false,
    changeOrderModal: false,
    requestModal: false
  });
  const toggleModal = (modalType, isOpen) => {
    setModals((prevState) => ({
      ...prevState,
      [modalType]: isOpen
    }));
  };
  // Fetch requests from the backend
  const fetchRequests = async (status = 'open', isloading = true, pageLoading = false, page = 1) => {
    const isPaginationRequest = page > 1;

    if (isPaginationRequest && paginationRequestInProgressRef.current) return;
    if (isPaginationRequest) paginationRequestInProgressRef.current = true;

    try {
      setError(null);
      // if (cache[status] && isloading) {
      //   setRequests(cache[status]);
      //   setLoading(false);
      //   return;
      // }
      setPageLoading(pageLoading);
      setLoading(isloading);

      const response = await APIServices.get(
        `/project-requests/${projectId}?requestStatus=${status}&page=${page}&limit=${PAGE_LIMIT}`
      ); // Replace with your actual API endpoint
      const data = response.data || [];
      setCache((prev) => ({ ...prev, [status]: data }));
      setRequests((prev) => (page === 1 ? data : [...prev, ...data]));
      setHasMore(data.length === PAGE_LIMIT);
      setCurrentPage(page);
    } catch (err) {
      console.error(err);
      setError('Failed to load requests');
    } finally {
      if (isPaginationRequest) paginationRequestInProgressRef.current = false;
      setLoading(false);
      setPageLoading(false);
    }
  };
  // Adjust scroll position after new data is loaded
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer && currentPage > 1) {
      const newScrollHeight = scrollContainer.scrollHeight;
      const previousScrollHeight = scrollContainer.dataset.previousScrollHeight;
      if (previousScrollHeight) {
        scrollContainer.scrollTop = scrollContainer.scrollTop + (newScrollHeight - previousScrollHeight);
      }
      scrollContainer.dataset.previousScrollHeight = newScrollHeight;
    }
  }, [requests]);

  useEffect(() => {
    if (projectId && currentTab == 2) {
      fetchRequests();
    }
  }, [projectId, currentTab]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        // Check if the scrollTop is at the top of the div
        if (
          Math.abs(scrollRef.current.scrollHeight - scrollRef.current.scrollTop - scrollRef.current.clientHeight) <=
            2 &&
          projectId &&
          !loading &&
          !paginationRequestInProgressRef.current &&
          hasMore
        ) {
          fetchRequests(activeTab, true, false, currentPage + 1); // Fetch the next page
        }
      }
    };

    const currentRef = scrollRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    // Clean up the event listener
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [currentPage, loading]);

  useEffect(() => {
    if (currentTab === 2 && activeTab && projectId) {
      fetchRequests(activeTab, false, true, 1);
    }
  }, [activeTab]);

  const getRequestHeading = (requestType = '') => {
    switch (requestType) {
      case 'sign_off':
        return 'Milestone Signoff:';
      case 'change_order':
        return 'Change Order Request';
      case 'change_request':
        return 'Request';
      default:
        return '';
    }
  };

  const getRequestBadge = (requestType = '') => {
    switch (requestType) {
      case 'sign_off':
        return 'Sign-Off';
      case 'change_order':
        return 'Change Order';
      case 'change_request':
        return 'Request';
      default:
        return '';
    }
  };

  const getCardColor = (requestType = '') => {
    switch (requestType) {
      case 'sign_off':
        return 'lightbluebg-card';
      case 'change_order':
        return 'purplebg-card';
      case 'change_request':
        return 'bluebg-card';
      default:
        return '';
    }
  };

  const updateandCloseModal = (modalType) => {
    setModals((prevState) => ({
      ...prevState,
      [modalType]: false
    }));
    fetchRequests();
  };

  useEffect(() => {
    activeTabRef.current = activeTab; // Keep the ref updated with the latest activeTab
  }, [activeTab]);

  const handleRequestEvent = (eventName, activity) => {
    if (activeTabRef.current && projectId) {
      fetchRequests(activeTabRef.current, false);
    }
    console.log(eventName, activity);
  };

  useEffect(() => {
    // Listen for 'projectActivityUpdate' event
    if (socket) {
      socket.on('ProjectRequestCreated', (activity) => handleRequestEvent('ProjectRequestCreated', activity));
      socket.on('ProjectRequestUpdated', (activity) => handleRequestEvent('ProjectRequestUpdated', activity));
      socket.on('ProjectRequestClosed', (activity) => handleRequestEvent('ProjectRequestClosed', activity));
    }
  }, [socket]);

  return (
    <div className="card-body-content request-content requestlist-content" ref={scrollRef}>
      {/* Tab Navigation */}
      <div className="bg-white btn-tab-group">
        <button
          className={`btn btn-custom ${activeTab === 'open' ? 'btn-dark' : 'btn-light'}`}
          onClick={() => setActiveTab('open')}>
          Open
        </button>
        <button
          className={`btn btn-custom  ${activeTab === 'closed' ? 'btn-dark' : 'btn-light'}`}
          onClick={() => setActiveTab('closed')}>
          Closed
        </button>
      </div>

      {/* Display loading, error or requests */}

      {error && <p className="error-message">{error}</p>}
      {!pageLoading && !requests.length && !loading && (
        <div className="nodata-loader h-100">
          <p>No requests yet. New requests will appear here. </p>
        </div>
      )}
      {!pageLoading ? (
        <div>
          {requests.length > 0 &&
            requests?.map((request) => (
              <div
                key={request?._id}
                className={`add_landing_card ${request?.unreadMessage > 0 && 'unread-card'}(
                request.requestType
              )}`}
                onClick={() => onRequestClick(request?._id, request?.parentRequestId)}>
                <div className="card_header">
                  {request?.requestType == 'sign_off' ? (
                    <h4>
                      {getRequestHeading(request?.requestType)} {request?.milestoneId?.name}
                    </h4>
                  ) : (
                    <h4>{request?.title}</h4>
                  )}
                  <div className="card_header_btn-group">
                    {!(request?.status === 'close' || request?.status === 'closed' || request?.status === 'rejected') &&
                      request?.clientReviewRequested && <a className="card_btn">Review Requested</a>}

                    {(request?.status === 'close' ||
                      request?.status === 'closed' ||
                      request?.status === 'rejected') && (
                      <a className={`card_btn `}>
                        {request?.requestType === 'change_request' ||
                        request?.requestType === 'change_order' ||
                        request?.requestType === 'sign_off'
                          ? request?.status === 'rejected'
                            ? 'Rejected'
                            : request?.isCanceled
                              ? 'Cancelled'
                              : 'Closed'
                          : 'Signed'}
                      </a>
                    )}

                    {/* </div> */}
                    {/* <a className="card_btn card_btn_green">
                    Signed
                  </a>
                  <a className="card_btn card_btn_red">
                   Quote Declined
                  </a> */}
                  </div>
                  <div className="head_right_btn">
                    <a className="card_btn">
                      {getRequestBadge(request.requestType)}
                      {/* // request.requestType == "change_request" ? "request" : request.requestType.replace("_", " ")} */}
                    </a>

                    {request.unreadMessage > 0 && <a className={`card_btn tag-unread`}>{request.unreadMessage}</a>}
                  </div>
                </div>
                <div className="card_cantent">
                  <p>{request.description}</p>
                  <span className="w-100">
                    Requested by {request?.createdBy?.fullName} .{' '}
                    {formatDate(request.createdDate || new Date(), true, store.currentDateFormat?.toUpperCase())}
                  </span>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div>{selectLoader(50)}</div>
      )}
      {loading && <div>{selectLoader(50)}</div>}
      {/* Bottom Actions */}
      <div className="user-chatBox-cover">
        <ul className="chat-btn-list">
          {hasPermission(userRole, 'send_signoff_requests', memberPermission) && (
            <li>
              <a className="cursor_pointer" onClick={() => toggleModal('signOffModal', true)}>
                + Sign-Off
              </a>
            </li>
          )}
          {hasPermission(userRole, 'change_order', memberPermission) && (
            <li>
              <a className="cursor_pointer" onClick={() => toggleModal('changeOrderModal', true)}>
                + Change Order
              </a>
            </li>
          )}
          {hasPermission(userRole, 'change_request', memberPermission) && (
            <li>
              <a className="cursor_pointer" onClick={() => toggleModal('requestModal', true)}>
                + Request
              </a>
            </li>
          )}
        </ul>
      </div>
      <SidebarModal
        extraClass={'edit-profile-modal'}
        show={modals.addQuoteModal}
        onHide={() => toggleModal('addQuoteModal', false)}
        headerTitle={'Quote'}>
        <AddQuoteModal
          projectId={projectId}
          changeReqId={changeReqId}
          closeModal={() => {
            updateandCloseModal('addQuoteModal');
          }}
        />
      </SidebarModal>
      {/* <SidebarModal
        extraClass={'edit-profile-modal'}
        show={modals.changeOrderModal}
        onHide={() => toggleModal('changeOrderModal', false)}
        headerTitle={'Change Order'}> */}
      <ModalCenter
        extraClass={'updatedRequest-modal'}
        show={modals.changeOrderModal}
        headerTitle={'Change Order'}
        closeModal={() => {
          toggleModal('changeOrderModal', false);
        }}>
        <ChangeOrderModal
          projectId={projectId}
          setChangeReqId={setChangeReqId}
          setQouteRequired={setQouteRequired}
          closeModal={() => {
            if (isQouteRequired == true) {
              setModals((prevState) => ({
                ...prevState,
                addQuoteModal: true,
                changeOrderModal: false
              }));
            } else {
              updateandCloseModal('changeOrderModal');
            }
          }}
        />
      </ModalCenter>
      {/* </SidebarModal> */}
      {/* <SidebarModal
        extraClass={'edit-profile-modal'}
        show={modals.requestModal}
        onHide={() => toggleModal('requestModal', false)}
        headerTitle={'New Request'}></SidebarModal> */}

      <ModalCenter
        extraClass={'updatedRequest-modal'}
        show={modals.requestModal}
        headerTitle={'Submit a Request'}
        // closeModal={() => {
        //   toggleModal('requestModal', false);
        // }}
        closeModal={() => {
          // call child logic instead
          document.dispatchEvent(new Event('requestModalClose'));
        }}>
        <RequestModal
          projectId={projectId}
          closeModal={() => {
            updateandCloseModal('requestModal');
          }}
          onCloseRequestModal={() => {
            toggleModal('requestModal', false);
          }}
        />
      </ModalCenter>
      <ModalCenter
        extraClass={'updatedSignOff-modal'}
        show={modals.signOffModal}
        headerTitle={'Sign-off Request'}
        closeModal={() => {
          toggleModal('signOffModal', false);
        }}>
        <SignOffForm
          projectId={projectId}
          isInternalProject={isInternalProject}
          closeModal={() => {
            updateandCloseModal('signOffModal');
          }}
        />
      </ModalCenter>
    </div>
  );
};

export default RequestListTab;
